/* ============================================================
   ENGINE UPDATE — the PURE core. `update(model, event, env)` →
   `{ model, effects }`. No Date.now, no RNG, no IO: every impurity
   arrives via the event (`now`, `snapshot`, `queue`, `channel`,
   `playerIndex`, settings) or `env` (the seeded resolvers). The shell
   (interpret.ts) applies the returned effects in list ORDER — order is
   the contract (e.g. `Cover` is emitted before `Swap`, mirroring the
   executor's mute-before-swap).

   This is a faithful transcription of runReconcile's convergence body
   (TvProvider) into a pure function: every `R.*` mutation becomes a
   model field write, every `dual.*`/overlay/timer/dispatch call becomes
   an effect. Phase 3's parity harness diffs this against the live
   executor before cutover.
   ============================================================ */
import { computeDesired, liveDriftSeconds, itemDurationSeconds } from '../live'
import { contentSecToVideoSec, videoSecToContentSec, effectivePortions, portionAdvance } from '../portions'
import { reconcile, type PlayerActual } from '../reconciler'
import { deriveCover } from '../cover'
import { adTailReclaim, MAX_LIVE_LAG_S } from '../catchup'
import { nextVideoIdAfter, queueEntries } from '../queue'
import { shouldFireIdent } from '../overlay'
import { liveSlot } from '../live'
import { SCENE_DURATIONS_MS } from '@/bumps/durations'
import {
  LIVE_LOCK_TOL_S,
  LIVE_LOSE_TOL_S,
  SEEK_COOLDOWN_MS,
  LAG_DEADBAND_S,
  LIVE_STALL_GRACE_MS,
  BUMPER_CHANNEL_MS,
  BLOCK_THRESHOLD,
} from './constants'
import type { Channel } from '../types'
import type { PlaybackModel } from './model'
import type { PlaybackEvent, EventContext } from './events'
import type { Effect } from './effects'

/** A resolved program ident (scene + its derived/seed length). */
export type IdentItem = { scene: string; params: Record<string, unknown>; maxMs?: number }

/** Injected impurities: the seeded-random resolvers the core composes (clock-free). */
export interface Env {
  /** Resolve the program ident for a channel (seeded random + avoid-repeat). Pure given lastBumper.
   *  NOT gated by `adBumpers` — the now-playing ident overlay is independent of ad injection. */
  resolveIdent: (
    channel: Channel,
    lastBumper: string | null,
  ) => { item: IdentItem | null; nextLastBumper: string | null }
  /** Resolve the channel-switch bump scene (seeded random + avoid-repeat). Pure given lastBumper. */
  resolveChannelBump: (
    channel: Channel,
    lastBumper: string | null,
  ) => { scene: string; params: Record<string, unknown>; nextLastBumper: string | null }
}

export interface UpdateResult {
  model: PlaybackModel
  effects: Effect[]
}

/** The shared Tick / PlayerSnapshot convergence — desired-vs-actual, as a pure step. */
const converge = (model: PlaybackModel, ev: EventContext, env: Env): UpdateResult => {
  const { now, queue, channel: ch, snapshot: snap, playerIndex, shuffle, adBumpers } = ev
  const m: PlaybackModel = { ...model }
  const effects: Effect[] = []

  // RECOVERY: a genuine PLAY breaks the error streak and lifts any systemic block.
  if (snap.fgState === 1) {
    m.consecutiveErrors = 0
    if (m.blocked) { m.blocked = false; effects.push({ e: 'SetBlocked', on: false }) }
  }
  // BLOCKED HALT: don't drive the player — freeze the errored frame and drop the click-catch so the
  // viewer can reach YouTube's sign-in/error UI. Only a genuine play (above) resumes convergence.
  if (m.blocked && snap.fgState !== 1) {
    if (m.screenInteractive) { m.screenInteractive = false; effects.push({ e: 'SetScreenInteractive', on: false }) }
    return { model: m, effects }
  }

  // ---- non-live autoplay advance (live items advance by wall-clock via computeDesired) ----
  if (!m.wantLive && m.manual && queue.length) {
    const n = queue.length
    const mi = (((m.manual.index % n) + n) % n)
    const item = queue[mi]
    if (item && (item.kind === 'scene' || item.kind === 'component')) {
      if (m.adStartedIndex !== mi) { m.adStartedIndex = mi; m.adStartedAt = now }
      if ((now - m.adStartedAt) / 1000 + m.manual.atSec >= itemDurationSeconds(item)) {
        m.manual = { index: mi + 1, atSec: 0 }
        m.adStartedIndex = null
      }
    } else {
      m.adStartedIndex = null
      if (snap.fgState === 0 && item && item.kind === 'video' && item.videoId === snap.fgId && snap.fgId !== m.lastEndedId) {
        m.lastEndedId = snap.fgId
        m.manual = { index: mi + 1, atSec: 0 }
      }
    }
  }
  if (snap.fgState === 1) m.lastEndedId = null

  const liveIntent = m.wantLive && !ch.noSignal && queue.length > 0
  const desired = computeDesired({
    queue,
    nowMs: liveIntent ? now - m.liveLag * 1000 : now, // live coasts on the lagged clock; manual unaffected
    live: liveIntent,
    manual: m.manual,
    paused: m.paused,
    noSignal: ch.noSignal,
    deadIds: m.deadIds,
  })

  // ---- live catch-up reclaim: drain the lag inside an ad's last 20% (once per ad) ----
  if (liveIntent && desired.kind === 'scene' && m.liveSynced) {
    if (desired.index !== m.lastReclaimedAdIndex) {
      const reclaim = adTailReclaim(m.liveLag, itemDurationSeconds(queue[desired.index]), desired.atSec)
      if (reclaim > 0) {
        m.liveLag = Math.max(0, m.liveLag - reclaim)
        m.lastReclaimedAdIndex = desired.index
      }
    }
  } else if (desired.kind !== 'scene') {
    m.lastReclaimedAdIndex = null // off the scene → re-arm
  }

  // CONTENT↔VIDEO seam: desired.atSec is a CONTENT-second and reconcile compares it to the foreground
  // time, so feed reconcile the fg time converted to content (identity for whole videos). Seek targets
  // it returns are content-seconds → converted back to video at every seek emit below (`toVideoSec`).
  const desiredPortions = desired.kind === 'video' ? desired.portions : []
  const fgContentTime = videoSecToContentSec(desiredPortions, snap.fgTime)
  const toVideoSec = (contentSec: number) => contentSecToVideoSec(desiredPortions, contentSec)
  const actual: PlayerActual = { fgId: snap.fgId, fgState: snap.fgState, fgTime: fgContentTime, bgId: snap.bgId, bgReady: snap.bgReady }
  const r = reconcile(desired, actual)
  if (desired.kind !== 'off') m.desiredIndex = desired.index
  if (liveIntent && r.onTarget && !m.liveSynced) m.liveSynced = true // ACQUIRE → TRACK edge

  // ---- cover is a pure derivation of the TUNE intent (not buffer state) ----
  const { cover, tune } = deriveCover(m.tune, desired.kind, r.onTarget, now)
  m.tune = tune
  // MUTE before SWAP: emit Cover first so a reveal can never be orphaned (order is the contract).
  effects.push({ e: 'Cover', on: cover !== null })

  const coverHolding = !!m.tune && now - m.tune.at < m.tune.holdMs

  // ---- converge the buffers (Stage / Swap / Adjust) ----
  if (r.prepare) effects.push({ e: 'StageBackground', id: r.prepare })
  if (r.swap && coverHolding) {
    // bumper still owns the screen — keep warming the airing item in standby, swap after the hold
    if (desired.kind === 'video') effects.push({ e: 'StageBackground', id: desired.id })
  } else if (r.swap) {
    effects.push({ e: 'Swap', index: desired.kind === 'video' ? desired.index : playerIndex, seekSec: toVideoSec(r.seekSec ?? 0) })
  } else if (r.seekSec != null && now - m.lastSeekAt > SEEK_COOLDOWN_MS) {
    // a forward seek = behind the lagged line. In TRACK, absorb it (coast) instead of seeking over
    // program content; in ACQUIRE (or past the lag cap, or a backward seek) take the real seek.
    const behind = liveIntent && desired.kind === 'video' ? desired.atSec - fgContentTime : -1
    if (m.liveSynced && behind > LAG_DEADBAND_S && m.liveLag + behind <= MAX_LIVE_LAG_S) {
      m.liveLag += behind
    } else {
      effects.push({ e: 'SeekForeground', sec: toVideoSec(r.seekSec) })
      m.lastSeekAt = now
    }
  }
  if (r.hideFrames) {
    // a scene/component is the content — the Stage's PresentScene/PresentComponent (below) hides the
    // frames; here we just keep the index bookkeeping honest for a non-live ENDED advance.
    if (desired.kind === 'scene' || desired.kind === 'component') effects.push({ e: 'SetIndex', index: desired.index })
  } else if (r.play && !r.swap && !coverHolding) {
    effects.push({ e: 'PlayForeground' })
  }
  if (desired.kind === 'off' && m.paused) effects.push({ e: 'PauseForeground' })

  // ---- ITEM IDENTITY = videoId#portionStart. Split halves of a video are DISTINCT items that happen
  //      to share a videoId. When the foreground holds the right video but is on a DIFFERENT item than
  //      desired (a prev/next between halves, or the after-half of a mid-content ad), REPOSITION it:
  //      seek to the new portion + retag the index + play — no swap (don't reload the same video). When
  //      it IS on the desired item, watch its portion boundaries (skip a removed gap, advance at content
  //      end). Whole videos carry no portions → this block is skipped (the YouTube ENDED event drives
  //      them). One rule for live and manual. ----
  if (desired.kind === 'video' && snap.fgId === desired.id && snap.fgState === 1 && desiredPortions.length >= 1) {
    const n = queue.length
    const fgItem = n ? queue[(((playerIndex % n) + n) % n)] : null
    const desStart = desiredPortions[0]?.start ?? 0
    const onDesiredItem =
      fgItem != null && fgItem.kind === 'video' && fgItem.videoId === desired.id &&
      (effectivePortions(fgItem)[0]?.start ?? 0) === desStart
    if (!onDesiredItem) {
      // right video, wrong item (sibling portion) → reposition the loaded frame onto it.
      if (now - m.lastSeekAt > SEEK_COOLDOWN_MS) {
        effects.push({ e: 'SeekForeground', sec: toVideoSec(desired.atSec) })
        effects.push({ e: 'SetIndex', index: desired.index })
        effects.push({ e: 'PlayForeground' })
        m.lastSeekAt = now
      }
      m.lastPortionAdvance = null
    } else {
      const step = portionAdvance(desiredPortions, snap.fgTime)
      if (step?.kind === 'seek') {
        const key = `${desired.index}:s${step.portionIndex}`
        if (m.lastPortionAdvance !== key && now - m.lastSeekAt > SEEK_COOLDOWN_MS) {
          effects.push({ e: 'SeekForeground', sec: step.videoSec })
          m.lastSeekAt = now
          m.lastPortionAdvance = key
        }
      } else if (step?.kind === 'end' && !m.wantLive && m.manual) {
        const key = `${desired.index}:end`
        if (m.lastPortionAdvance !== key) {
          m.lastPortionAdvance = key
          m.manual = { index: (((m.manual.index % n) + n) % n) + 1, atSec: 0 } // content over → next item
        }
      } else if (step === null) {
        m.lastPortionAdvance = null // back inside a portion → re-arm
      }
    }
  } else {
    m.lastPortionAdvance = null
  }

  // ---- anticipate: pre-buffer the next video while stable (instant next swap) ----
  if ((r.onTarget || desired.kind === 'scene') && queue.length) {
    const baseIndex = desired.kind === 'off' ? playerIndex : desired.index
    const nextId = nextVideoIdAfter(queue, baseIndex)
    if (nextId && snap.bgId !== nextId) effects.push({ e: 'StageBackground', id: nextId })
  }

  // ---- program ident edge: announce a new in-channel video, once, with no cover ----
  if (snap.fgId && shouldFireIdent({
    cover,
    desiredKind: desired.kind,
    onTarget: r.onTarget,
    fgId: snap.fgId,
    desiredId: desired.kind === 'video' ? desired.id : null,
    lastInterstitialVideo: m.lastInterstitialVideo,
  })) {
    m.lastInterstitialVideo = snap.fgId
    const { item, nextLastBumper } = env.resolveIdent(ch, m.lastBumper)
    m.lastBumper = nextLastBumper
    if (!item) {
      m.identActive = false
      m.identItem = null
    } else {
      m.identItem = item
      m.identActive = true
      effects.push({ e: 'CancelTimer', id: 'ident' })
      effects.push({ e: 'ScheduleTimer', id: 'ident', ms: SCENE_DURATIONS_MS[item.scene] ?? item.maxMs ?? 0 })
    }
  }

  // ---- THE FULL-FRAME CONTENT OCCUPANT — the Stage owns EXACTLY ONE, modelled explicitly so every
  //      state maps to a real effect (no nullable "leave it" that conflated snow / pause / teardown).
  //        scene  — the tune COVER or an ad scene
  //        component — HIDEO
  //        video  — the active iframe (also the resting state of a paused/landed channel)
  //        blank  — NO content at all: a no-signal channel (snow owns the screen)
  //      `occupant === null` is the explicit HOLD: a transient empty queue (programs loading) or a
  //      paused freeze → keep what's up (pause is transport, handled by PauseForeground, not an occupant
  //      change). Emitted on key change. ----
  type StageContent =
    | { key: string; effect: Effect } // a concrete occupant
    | null // hold the current occupant (transient-empty / paused)
  const occupant: StageContent =
    ch.noSignal
      ? { key: 'blank', effect: { e: 'BlankStage' } } // genuinely no signal → static owns the screen
      : cover === 'channel' && m.tune
        ? { key: `cover:${ch.number}:${m.tune.at}`, effect: { e: 'PresentScene', scene: m.tune.scene, params: m.tune.params, startSec: 0 } }
        : desired.kind === 'scene' && r.ad
          ? { key: `ad:${desired.index}`, effect: { e: 'PresentScene', scene: r.ad.scene, params: r.ad.params, startSec: r.ad.atSec } }
          : desired.kind === 'component' && r.component
            ? { key: `component:${desired.index}`, effect: { e: 'PresentComponent', component: r.component.component } }
            : desired.kind === 'video'
              ? { key: 'video', effect: { e: 'RevealVideo' } }
              : queue.length === 0 || m.paused
                ? null // HOLD: a transient empty queue (programs still loading — grace) or a paused freeze.
                : { key: 'video', effect: { e: 'RevealVideo' } } // off for other reasons (boot/live no-slot)
  if (occupant && occupant.key !== m.contentKey) {
    m.contentKey = occupant.key
    effects.push(occupant.effect)
  }

  // ---- the now-playing IDENT card — the LONE overlay, composited OVER the playing video (no cover). It
  //      shows ONLY over the EXACT video it announced, so a NEXT/PREV (the desired video changes) hides it
  //      at once, not when its timer expires. (A transition also tears it down structurally in the Stage.)
  const cardKey =
    m.identActive && m.identItem && cover === null && desired.kind === 'video' && desired.id === m.lastInterstitialVideo
      ? `ident:${m.lastInterstitialVideo}`
      : null
  if (cardKey !== m.shownKey) {
    m.shownKey = cardKey
    if (cardKey && m.identItem) effects.push({ e: 'ShowOverlay', kind: 'ident', key: cardKey, scene: m.identItem.scene, params: m.identItem.params })
    else effects.push({ e: 'HideOverlay' })
  }

  // ---- screen / playback, emitted once-on-change ----
  if (!ch.noSignal) {
    const picture = cover === null && (r.onTarget || (desired.kind === 'video' && snap.fgState === 1))
    const nextScreen = cover === 'channel' ? 'tuning' : (cover === 'ad' || picture) ? 'playing' : null
    const nextPlayback = cover === 'channel' ? 'tuning' : cover === 'ad' ? 'ad' : picture ? 'playing' : null
    if (nextScreen && m.screen !== nextScreen) { m.screen = nextScreen; effects.push({ e: 'SetScreen', mode: nextScreen }) }
    if (nextPlayback && m.playback !== nextPlayback) { m.playback = nextPlayback; effects.push({ e: 'SetPlayback', phase: nextPlayback }) }
  }

  // ---- the picture is ours-to-control (playing or we-paused) → the screen click-catch is enabled;
  //      else YouTube has the wheel (bot-check / sign-in / error / stall) and clicks fall through.
  //      Emitted BEFORE the stall-grace early-return so a block always lifts the shield. ----
  const interactive = snap.fgState === 1 || m.paused
  if (interactive !== m.screenInteractive) { m.screenInteractive = interactive; effects.push({ e: 'SetScreenInteractive', on: interactive }) }

  // ---- the LIVE badge: timeline proximity with a transient-blip grace + Schmitt hysteresis ----
  // a live full-screen content segment (a scene OR a component) lights the badge directly — there's no
  // video frame to "follow", so proximity is just the schedule lag being inside tolerance.
  const onLiveAd = liveIntent && (desired.kind === 'scene' || desired.kind === 'component') && m.liveLag <= LIVE_LOCK_TOL_S
  const following = snap.fgState === 1 || snap.fgState === 3
  if (following) m.lastFollowingAt = now
  const transient = snap.fgState === -1 || snap.fgState === 0 || snap.fgState === 5
  if (!onLiveAd && !following && transient && now - m.lastFollowingAt < LIVE_STALL_GRACE_MS) {
    return { model: m, effects } // a spin-up blip: hold the badge, skip its derivation this tick
  }
  const onBroadcast = following && !ch.noSignal && !shuffle && adBumpers && queue.length > 0
  // the live schedule is in CONTENT-seconds, so the player's position must be too — convert fg video
  // time through the player item's portions (identity for whole videos; the fix for split/sponsor items
  // whose video time ≠ content time, e.g. the Night Desk's long clips never lit the LIVE badge).
  const pItem = queue.length ? queue[(((playerIndex % queue.length) + queue.length) % queue.length)] : null
  // use the REAL portions (empty = whole video = pure identity, no clamp — keeps the existing badge
  // behaviour for normal videos); only split/sponsor items convert.
  const playerContentSec = pItem && pItem.kind === 'video' ? videoSecToContentSec(pItem.portions ?? [], snap.fgTime) : snap.fgTime
  const drift = onBroadcast ? liveDriftSeconds(queue, playerIndex, playerContentSec, now) : null
  const tol = m.live ? LIVE_LOSE_TOL_S : LIVE_LOCK_TOL_S
  const badge = onLiveAd || (drift !== null && Math.abs(drift) <= tol)
  if (badge !== m.live) {
    m.live = badge
    effects.push({ e: 'Log', message: badge ? 'live acquired' : 'live lost', data: { drift, onBroadcast, following, playerState: snap.fgState } })
    effects.push({ e: 'SetLive', on: badge })
  }
  return { model: m, effects }
}

/** A channel LANDING (the shell navigated + built the target `queue`/`channel`). A DIFFERENT-channel
 *  tune ALWAYS stamps a fresh bumper (bumper always plays on a switch), then converges. */
const tune = (model: PlaybackModel, ev: Extract<PlaybackEvent, { t: 'Tune' }>, env: Env): UpdateResult => {
  const m: PlaybackModel = { ...model }
  const bump = env.resolveChannelBump(ev.channel, m.lastBumper)
  m.lastBumper = bump.nextLastBumper
  const slot = ev.live ? liveSlot(ev.queue, ev.now) : null
  const landIndex = ev.live ? slot?.index ?? 0 : ev.manualIndex ?? 0
  m.tune = {
    at: ev.now,
    holdMs: SCENE_DURATIONS_MS[bump.scene] ?? BUMPER_CHANNEL_MS,
    targetIndex: landIndex,
    scene: bump.scene,
    params: bump.params,
  }
  m.wantLive = ev.live
  m.manual = ev.live ? null : { index: landIndex, atSec: 0 }
  m.paused = false
  m.liveSynced = false // a fresh landing ACQUIRES before it can coast
  m.liveLag = 0
  m.lastReclaimedAdIndex = null
  const adopt: Effect = { e: 'AdoptQueue', ids: queueEntries(ev.queue), index: landIndex }
  const out = converge(m, ev, env)
  return { model: out.model, effects: [adopt, ...out.effects] }
}

/** A playback-intent remote verb. Same-channel deliberate inputs CANCEL an in-flight cover
 *  (`tune=null`) so they converge immediately — the rule that makes "LIVE skips the bumper" and
 *  "prog-step collapses the bumper" fall out for free. Non-playback verbs are handled elsewhere. */
const remote = (model: PlaybackModel, ev: Extract<PlaybackEvent, { t: 'Remote' }>, env: Env): UpdateResult => {
  const m: PlaybackModel = { ...model }
  const pre: Effect[] = []
  switch (ev.action) {
    case 'live': // LIVE button — go live NOW on the current channel; collapse any bumper
      pre.push({
        e: 'AdoptQueue',
        ids: queueEntries(ev.queue),
        index: liveSlot(ev.queue, ev.now)?.index ?? ev.playerIndex,
      })
      m.wantLive = true
      m.manual = null
      m.paused = false
      m.liveSynced = false
      m.liveLag = 0
      m.lastReclaimedAdIndex = null
      m.tune = null
      break
    case 'progNext':
    case 'progPrev': // hand-steer the transport by VIDEO; drop live, collapse the cover
      m.wantLive = false
      m.paused = false
      m.liveSynced = false
      m.tune = null
      m.manual = { index: ev.playerIndex + (ev.action === 'progNext' ? 1 : -1), atSec: 0 }
      break
    case 'play':
    case 'ok': // toggle pause; pausing captures the current position as the manual one
      if (m.tune) return converge(m, ev, env) // play/pause does not cancel a channel bumper
      m.wantLive = false
      m.tune = null
      if (!m.paused) {
        // capture the pause position in CONTENT-seconds (so manual.atSec is uniformly content).
        const n = ev.queue.length
        const it = n ? ev.queue[(((ev.playerIndex % n) + n) % n)] : null
        const atSec = it && it.kind === 'video' ? videoSecToContentSec(effectivePortions(it), ev.snapshot.fgTime) : ev.snapshot.fgTime
        m.manual = { index: ev.playerIndex, atSec }
        m.paused = true
      } else {
        m.paused = false
      }
      break
    default:
      return { model, effects: [] } // not a playback-intent verb (UI action handled elsewhere)
  }
  const out = converge(m, ev, env)
  return { model: out.model, effects: [...pre, ...out.effects] }
}

/** Explicit same-channel program choice (LCD/guide current channel): this replaces the playback
 *  intent, so it cancels any in-flight channel bumper and converges immediately. */
const programPick = (model: PlaybackModel, ev: Extract<PlaybackEvent, { t: 'ProgramPick' }>, env: Env): UpdateResult => {
  const m: PlaybackModel = {
    ...model,
    wantLive: false,
    manual: { index: ev.index, atSec: 0 },
    paused: false,
    liveSynced: false,
    tune: null,
  }
  const out = converge(m, ev, env)
  return {
    model: out.model,
    effects: [{ e: 'AdoptQueue', ids: queueEntries(ev.queue), index: ev.index }, ...out.effects],
  }
}

const timerFired = (model: PlaybackModel, ev: Extract<PlaybackEvent, { t: 'TimerFired' }>, env: Env): UpdateResult => {
  if (ev.id !== 'ident') return converge(model, ev, env)
  return converge({ ...model, identActive: false, identItem: null }, ev, env)
}

/** A frame failed after the player's retries. A single failure marks the video dead (skip it); but
 *  consecutive failures with no genuine play between = a SYSTEMIC block (IP bot-flag → error 150 on
 *  everything), so at the threshold we HALT instead of churning the queue. */
const error = (model: PlaybackModel, ev: Extract<PlaybackEvent, { t: 'Error' }>, env: Env): UpdateResult => {
  const m: PlaybackModel = { ...model, consecutiveErrors: model.consecutiveErrors + 1 }
  if (m.consecutiveErrors >= BLOCK_THRESHOLD) {
    m.deadIds = new Set() // systemic, not per-video — re-test everything once unblocked
    const pre: Effect[] = m.blocked ? [] : [{ e: 'SetBlocked', on: true }]
    m.blocked = true
    const out = converge(m, ev, env) // converges into the halt (freeze + drop shield)
    return { model: out.model, effects: [...pre, ...out.effects] }
  }
  const dead = new Set(m.deadIds)
  if (ev.videoId) dead.add(ev.videoId)
  m.deadIds = dead
  return converge(m, ev, env) // route around the dead video → next
}

/** The pure step. Other events (Unlock/Power/QueueChanged/TimerFired UI bits) port in later phases. */
export const update = (model: PlaybackModel, event: PlaybackEvent, env: Env): UpdateResult => {
  switch (event.t) {
    case 'Tick':
    case 'PlayerSnapshot':
      return converge(model, event, env)
    case 'Tune':
      return tune(model, event, env)
    case 'Remote':
      return remote(model, event, env)
    case 'ProgramPick':
      return programPick(model, event, env)
    case 'Error':
      return error(model, event, env)
    case 'TimerFired':
      return timerFired(model, event, env)
    default:
      return { model, effects: [] }
  }
}
